import * as React from "react";
import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

interface AnimatedTextCycleProps {
  words: string[];
  interval?: number;
  className?: string;
}

export default function AnimatedTextCycle({
  words,
  interval = 5000,
  className = "",
}: AnimatedTextCycleProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [maxWidth, setMaxWidth] = useState<number | null>(null);
  const measureRef = useRef<HTMLDivElement>(null);

  // Calculate the max width of all words
  useEffect(() => {
    if (measureRef.current) {
      let maxW = 0;
      const elements = measureRef.current.children;
      for (let i = 0; i < elements.length; i++) {
        const wordWidth = elements[i].getBoundingClientRect().width;
        if (wordWidth > maxW) {
          maxW = wordWidth;
        }
      }
      // Add a small buffer to prevent potential wrapping/jitter
      setMaxWidth(maxW + 2);
    }
  }, [words, className]); // Depend on words and className

  // Cycle through words
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % words.length);
    }, interval);

    return () => clearInterval(timer);
  }, [interval, words.length]);

  // Container animation for the whole word (fade/blur effect)
  const containerVariants = {
    hidden: {
      y: -10, // Reduced vertical movement
      opacity: 0,
      filter: "blur(5px)" // Reduced blur
    },
    visible: {
      y: 0,
      opacity: 1,
      filter: "blur(0px)",
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    },
    exit: {
      y: 10, // Reduced vertical movement
      opacity: 0,
      filter: "blur(5px)", // Reduced blur
      transition: {
        duration: 0.3,
        ease: "easeIn"
      }
    },
  };

  return (
    <>
      {/* Hidden measurement div */}
      <div
        ref={measureRef}
        aria-hidden="true"
        className="absolute opacity-0 pointer-events-none -z-10"
        style={{ visibility: "hidden" }}
      >
        {words.map((word, i) => (
          <span key={i} className={`font-bold ${className}`}>
            {word}
          </span>
        ))}
      </div>

      {/* Visible animated word container (fixed width) */}
      <motion.span
        // Apply the calculated max width
        style={{ width: maxWidth !== null ? `${maxWidth}px` : 'auto' }}
        className="relative inline-block align-bottom text-center" // Added text-center
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={currentIndex}
            className={`inline-block font-bold w-full ${className}`} // Added w-full
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            style={{ whiteSpace: "nowrap" }}
          >
            {words[currentIndex]}
          </motion.span>
        </AnimatePresence>
      </motion.span>
    </>
  );
}
